import { Avatar, Divider, Typography } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./ConnectPractitionerCard.styles";

interface Props {
  pracName: string;
  pracAvatar?: string;
  pracFirstName?: string;
  pracLastName?: string;
  practitionerType?: string;
  location?: string;
  firstVisitPrice?: string;
  isFirstVisitFree?: boolean;
  subsequentVisitPrice?: string;
  isSubsequentVisitFree?: boolean;
}

const ConnectPractitionerCard = ({
  pracName,
  pracAvatar,
  pracFirstName,
  pracLastName,
  practitionerType,
  location,
  firstVisitPrice,
  isFirstVisitFree,
  subsequentVisitPrice,
  isSubsequentVisitFree,
}: Props) => {
  const { t } = useTranslation();

  const hasPricing = !!firstVisitPrice || !!subsequentVisitPrice;

  const renderAvatar = () => {
    const alt = t(l.connect.pracAvatarAlt, { pracName });

    if (pracAvatar) return <Avatar imageUrl={pracAvatar} size="medium" alt={alt} />;

    if (!pracFirstName || !pracLastName) return null;

    return <Avatar firstName={pracFirstName} lastName={pracLastName} size="medium" alt={alt} />;
  };

  const renderTypeAndLocation = () => {
    if (!practitionerType || !location) return null;

    return (
      <Typography variant="footnote" colorIntensity="subtle" isMarginless>
        {t(l.connect.practitionerCard.typeAndLocation, {
          practitionerType,
          location,
          interpolation: { escapeValue: false },
        })}
      </Typography>
    );
  };

  const renderPriceRow = (visitLabel: string, price: string, isFree?: boolean) => (
    <div css={styles.pricingRow}>
      <Typography variant="footnote" isMarginless>
        {visitLabel}
      </Typography>
      <Typography
        variant="footnote"
        fontWeight="bold"
        isMarginless
        {...(isFree ? { customCss: styles.freePrice } : { colorIntensity: "bold" })}
      >
        {price}
      </Typography>
    </div>
  );

  const renderPricing = () => {
    if (!hasPricing) return null;

    return (
      <>
        <Divider customCss={styles.divider} />
        <div css={styles.pricing}>
          {!!firstVisitPrice &&
            renderPriceRow(
              t(l.connect.practitionerCard.firstVisit),
              firstVisitPrice,
              isFirstVisitFree
            )}
          {!!subsequentVisitPrice &&
            renderPriceRow(
              t(l.connect.practitionerCard.subsequentVisit),
              subsequentVisitPrice,
              isSubsequentVisitFree
            )}
        </div>
      </>
    );
  };

  return (
    <div css={styles.card}>
      <div css={styles.identity}>
        {renderAvatar()}
        <div>
          <Typography variant="footnote" colorIntensity="subtle" isMarginless>
            {t(l.connect.practitionerCard.requestingAppointmentWith)}
          </Typography>
          <Typography as="h2" variant="h3" colorIntensity="bold" isMarginless>
            {pracName}
          </Typography>
          {renderTypeAndLocation()}
        </div>
      </div>
      {renderPricing()}
    </div>
  );
};

export { ConnectPractitionerCard };
export type { Props as ConnectPractitionerCardProps };
